import {
  IconAlertTriangle,
  IconClockExclamation,
  IconHeadset,
  IconLockAccess,
  IconRefresh,
  IconServerOff,
  IconWifiOff,
} from "@tabler/icons-react";
import I18n from "i18n-js";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useFetchMeData } from "@common/features/User";
import { getApiErrorState } from "@common/helpers/api";
import { versionCompare } from "@common/helpers/functions";
import { __, language, setTimeZone } from "@common/helpers/i18n";
import { removeInitialSplash } from "@common/helpers/splash";
import { ErrorState, LoadingState } from "@app/elements/container";
import { Button } from "@app/elements/form";
import {
  getDirectionFromLocale,
  getLocaleFromPublicPath,
} from "@app/helpers/publicLocale";
import { syncDocumentDirection } from "@app/hooks";

const NewFeatureModal = lazy(() =>
  import("@app/elements/modals/NewFeatureModal").then((module) => ({
    default: module.NewFeatureModal,
  })),
);

export default function UserDataProvider({
  installedVersion,
  onInstall,
  children,
}: {
  installedVersion?: string;
  onInstall?: (version: string) => void;
  children: JSX.Element;
}) {
  const [_language, _setLanguage] = useState(language);
  const [loadedLanguage, setLoadedLanguage] = useState<string>();
  const publicRouteLocale = getLocaleFromPublicPath();

  const { data, error, isError, isFetching, refetch } = useFetchMeData(
    undefined,
    undefined,
    false,
  );

  const isLoaded = data !== undefined;
  const isPreparingUserData = !isLoaded || !loadedLanguage;

  useEffect(() => {
    if (data) {
      if (data.masterUser) {
        setTimeZone(data.masterUser.timezone);
      }

      const nextLanguage = publicRouteLocale || data.language || language;

      syncDocumentDirection({
        direction: publicRouteLocale
          ? getDirectionFromLocale(publicRouteLocale)
          : data.is_rtl,
        language: nextLanguage,
      });

      if (nextLanguage !== _language) {
        _setLanguage(nextLanguage);
      }
    }
  }, [data, _language, publicRouteLocale]);

  useEffect(() => {
    if (_language && loadedLanguage !== _language) {
      import(`@app/assets/locale/${_language}.json`)
        .then((module) => {
          I18n.translations = {
            ...I18n.translations,
            [_language]: {
              ...(I18n.translations[_language] || {}),
              ...module.default,
              ...{
                [import.meta.env.VITE_SITE_NAME]: import.meta.env
                  .VITE_SITE_TITLE,
              },
            },
          };
          I18n.locale = _language;
          __.cache.clear?.();
          setLoadedLanguage(_language);
        })
        .catch(() => {
          I18n.locale = _language;
          __.cache.clear?.();
          setLoadedLanguage(_language);
        });
    }
  }, [_language, loadedLanguage]);

  useEffect(() => {
    if ((loadedLanguage && isLoaded) || isError) {
      removeInitialSplash();
    }
  }, [isError, isLoaded, loadedLanguage]);

  const isNewVersion = useMemo(
    () =>
      (installedVersion &&
        data?.user &&
        data.webapp?.version &&
        versionCompare(installedVersion, data.webapp.version) < 0) ||
      false,
    [data, installedVersion],
  );
  const errorState = useMemo(() => getApiErrorState(error), [error]);
  const ErrorIcon =
    errorState.kind === "offline" || errorState.kind === "network"
      ? IconWifiOff
      : errorState.kind === "timeout"
        ? IconClockExclamation
        : errorState.kind === "server"
          ? IconServerOff
          : errorState.kind === "unauthorized" ||
              errorState.kind === "forbidden"
            ? IconLockAccess
            : IconAlertTriangle;
  const contactUrl = `${import.meta.env.VITE_SITE_URL.replace(/\/$/, "")}/contact`;

  if (isError && !data) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center bg-background px-4 py-6 text-foreground sm:px-6">
        <ErrorState
          action={
            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
              {errorState.kind === "server" && (
                <Button
                  href={contactUrl}
                  variant="light"
                  icon={<IconHeadset size={18} stroke={2} />}
                  className="justify-center"
                >
                  {__("Contact us")}
                </Button>
              )}
              <Button
                type="button"
                variant="primary"
                loading={isFetching}
                onClick={() => void refetch()}
                icon={<IconRefresh size={18} stroke={2} />}
                className="justify-center"
              >
                {__("Try again")}
              </Button>
            </div>
          }
          description={errorState.description}
          icon={ErrorIcon}
          size="page"
          surface="card"
          className="max-w-2xl"
          title={errorState.title}
          tone={errorState.tone}
        />
      </div>
    );
  }

  if (isPreparingUserData) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center bg-background px-4 py-6 text-foreground">
        <LoadingState size="page" surface="plain" />
      </div>
    );
  }

  return (
    <>
      {data && isNewVersion && (
        <Suspense fallback={null}>
          <NewFeatureModal
            isOpen={isNewVersion}
            onClose={() => onInstall?.(data.webapp?.version)}
            title={data.webapp?.title}
            subtitle={data.webapp?.subtitle}
            description={data.webapp?.description}
          />
        </Suspense>
      )}
      {children}
    </>
  );
}
